import React, { useRef, useEffect } from 'react';
import styles from './index.module.less';

interface VideoRendererProps {
  /** 视频源URL */
  videoSrc: string;
  /** 容器宽度 */
  width: number;
  /** 容器高度 */
  height: number;
  /** 是否可以开始播放 */
  canStart: boolean;
  /** 视频切换完成回调 */
  onVideoChange?: () => void;
  /** 额外的样式类名 */
  className?: string;
}

/**
 * 视频渲染组件
 * 使用原生 video 元素替代 Canvas，提供更好的画质
 *
 * 性能优化：使用 React.memo 避免不必要的重渲染
 */
const VideoRenderer = React.memo(function VideoRenderer({
  videoSrc,
  width,
  height,
  canStart,
  onVideoChange,
  className,
}: VideoRendererProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // 视频源变化处理和资源清理
  useEffect(() => {
    if (!canStart || !videoSrc || !videoRef.current) return;

    const video = videoRef.current;

    // 如果视频源相同，不需要重新加载
    if (video.src.endsWith(videoSrc)) return;

    const handleCanPlay = () => {
      try {
        video.play();
        onVideoChange?.();
      } catch (error) {
        console.warn('Video play failed:', error);
      }
    };

    const handleLoadedMetadata = () => {
      // 视频元数据加载完成，可以获取视频尺寸
      console.log('Video dimensions:', video.videoWidth, 'x', video.videoHeight);
    };

    video.addEventListener('canplay', handleCanPlay, { once: true });
    video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    video.src = videoSrc;
    video.load();

    // 完整的清理逻辑：清理事件监听器和视频资源
    return () => {
      // 移除事件监听器
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);

      // 清理视频资源，防止内存泄漏
      try {
        video.pause();
        video.src = '';
        video.load();
      } catch (error) {
        // 忽略清理过程中的错误（可能元素已被移除）
        console.debug('Video cleanup error (可忽略):', error);
      }
    };
  }, [videoSrc, canStart, onVideoChange]);

  // 事件处理函数
  const handleVideoLoaded = () => {
    console.log('Video loaded successfully');
  };

  const handleVideoError = (error: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error('Video loading error:', error);
  };

  return (
    <video
      ref={videoRef}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        objectPosition: 'center',
        borderRadius: 'inherit',
        background: 'transparent',
      }}
      muted
      playsInline
      autoPlay
      loop
      onLoadedData={handleVideoLoaded}
      onError={handleVideoError}
      className={`${styles.videoElement} ${className || ''}`}
    />
  );
}, (prevProps, nextProps) => {
  // 自定义比较函数：仅当这些关键 props 都相同时才跳过重渲染
  return (
    prevProps.videoSrc === nextProps.videoSrc &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.canStart === nextProps.canStart &&
    prevProps.className === nextProps.className
    // onVideoChange 是回调函数，通常每次都会创建新引用，不参与比较
  );
});

export default VideoRenderer;
export type { VideoRendererProps };
